import { writable } from 'svelte/store';
import { getAiSuggestions } from '$lib/services/ai';
import { tasksStore } from './tasks';
import { historyStore } from './history';
import type { HistoryEntry, Task } from '$lib/domain/types';
import type { AIInsightCard } from '$lib/domain/blueprint';

const apiBaseUrl = import.meta.env.VITE_AI_BASE_URL ?? import.meta.env.VITE_API_BASE_URL ?? '';
const configured = Boolean(apiBaseUrl);

interface AiState {
  loading: boolean;
  suggestions: string[];
  cards: AIInsightCard[];
  configured: boolean;
  error?: string;
  provider?: string;
  model?: string;
  generatedAt?: string;
  notice?: string;
}

const createInsightCards = (suggestions: string[], generatedAt: string): AIInsightCard[] =>
  suggestions.map((body, index) => ({
    id: `ai-${index}-${generatedAt}`,
    title: index === 0 ? 'Next move' : index === 1 ? 'Plan signal' : 'Friction check',
    body,
    status: 'ready',
    updatedAt: generatedAt,
  }));

const loadingCards: AIInsightCard[] = [
  {
    id: 'ai-loading-1',
    title: 'Reading the focus graph',
    body: 'Scanning open tasks, recent sessions, and plan timing.',
    status: 'loading',
  },
  {
    id: 'ai-loading-2',
    title: 'Composing guidance',
    body: 'Preparing concise suggestions without changing your data.',
    status: 'loading',
  },
];

const createAiStore = () => {
  const { subscribe, set, update } = writable<AiState>({
    loading: false,
    suggestions: [],
    cards: [],
    configured,
  });

  const fetchSuggestions = async () => {
    update((state) => ({
      ...state,
      loading: true,
      error: undefined,
      cards: state.cards.length
        ? state.cards.map((card) => ({
            ...card,
            status: 'loading' as const,
            updatedAt: new Date().toISOString(),
          }))
        : loadingCards.map((card) => ({ ...card, updatedAt: new Date().toISOString() })),
    }));

    let tasks: Task[] = [];
    let history: HistoryEntry[] = [];
    const unsubTasks = tasksStore.subscribe((value) => (tasks = value));
    const unsubHistory = historyStore.subscribe((value) => (history = value));
    unsubTasks();
    unsubHistory();

    try {
      const payload = {
        tasks,
        history,
      };
      const response = await getAiSuggestions(apiBaseUrl, payload);
      const generatedAt = new Date().toISOString();
      const suggestions = response.suggestions ?? [];
      set({
        loading: false,
        suggestions,
        cards: createInsightCards(suggestions, generatedAt),
        configured,
        provider: response.ai?.provider,
        model: response.ai?.model,
        generatedAt,
        notice: response.notice,
      });
    } catch (error) {
      const message = (error as Error).message;
      set({
        loading: false,
        suggestions: [],
        cards: loadingCards.map((card) => ({
          ...card,
          status: 'failed',
          body: message,
          updatedAt: new Date().toISOString(),
        })),
        configured,
        error: message,
      });
    }
  };

  return { subscribe, fetchSuggestions };
};

export const aiStore = createAiStore();
